// Clients: the list, and one client's overview (stage, allowed sign-in domains, people with access).
// CRM, products and projects attach here in the next phases.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { Icon, Modal, ago, useConfirm, useToast } from '../ui';
import type { Client, Stage, UserRow } from '../types';
import { STAGE_LABELS } from '../types';
import { Page, useCrumbs } from './Shell';
import { ActivityTab, ContactsTab, CrmGlance } from './Crm';
import { NextUp, TasksTab } from './Tasks';
import { OpportunitiesTab } from './Pipeline';
import { InviteDialog } from './People';
import { KeyFacts, ResearchTab } from './Research';
import { KitsTab } from './Kits';
import { ExportsCard } from './Exports';
import { UnfiledEmails } from './Email';
import { ClientProjects } from './Projects';

const STAGE_BADGE: Record<Stage, string> = { lead: 'info', prospect: 'brand', active: 'ok', past: '', disqualified: 'bad' };

export function ClientList() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const setCrumbs = useCrumbs();
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState<Stage | ''>('');
  const [creating, setCreating] = useState(false);
  useLiveTopics('workspace');
  useEffect(() => setCrumbs([{ label: 'Clients' }]), [setCrumbs]);
  const { data: clients = [], isLoading } = useQuery({ queryKey: ['clients'], queryFn: () => get<Client[]>('/api/clients') });
  const shown = clients.filter((c) => (!stage || c.stage === stage) && `${c.name} ${c.website ?? ''} ${c.domains.join(' ')}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <Page title="Clients" eyebrow="Workspace" actions={can('clients.edit') && <button className="btn primary" onClick={() => setCreating(true)}><Icon name="plus" />New client</button>}>
      {can('crm.view') && <UnfiledEmails clients={clients} />}
      <div className="row wrap" style={{ marginBottom: 14 }}>
        <label className="row input" style={{ maxWidth: 320, padding: '0 10px', gap: 6 }}>
          <Icon name="search" />
          <input aria-label="Search clients" placeholder="Search clients" value={query} onChange={(e) => setQuery(e.target.value)} style={{ border: 0, outline: 0, background: 'none', flex: 1, minWidth: 0, height: 36 }} />
        </label>
        <select className="input" style={{ width: 'auto' }} value={stage} onChange={(e) => setStage(e.target.value as Stage | '')} aria-label="Filter by stage">
          <option value="">All stages</option>
          {(Object.keys(STAGE_LABELS) as Stage[]).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
      </div>
      <div className="card table-wrap">
        <table className="table">
          <thead><tr><th>Client</th><th>Stage</th><th>Website</th><th>Sign-in domains</th><th>Updated</th></tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className="clickable" onClick={() => navigate(`/clients/${c.id}`)}>
                <td><Link to={`/clients/${c.id}`} onClick={(e) => e.stopPropagation()}><b>{c.name}</b></Link></td>
                <td><span className={`badge ${STAGE_BADGE[c.stage]}`}>{STAGE_LABELS[c.stage]}</span></td>
                <td className="muted">{c.website ? c.website.replace(/^https?:\/\//, '') : '–'}</td>
                <td className="muted mono">{c.domains.join(', ') || '–'}</td>
                <td className="muted">{ago(c.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && !shown.length && <div className="empty">{clients.length ? 'No clients match.' : 'No clients yet.'}</div>}
      </div>
      {creating && <NewClientDialog onClose={() => setCreating(false)} onCreated={(c) => navigate(`/clients/${c.id}`)} />}
    </Page>
  );
}

function NewClientDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (c: Client) => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [stage, setStage] = useState<Stage>('lead');
  const create = useMutation({
    mutationFn: () => post<Client>('/api/clients', { name, website: website || null, stage }),
    onSuccess: (c) => { toast(`Created ${c.name}`); onCreated(c); },
    onError: (e: Error) => toast(e.message, true),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };
  return (
    <Modal title="New client" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <label className="field"><span>Company name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></label>
        <label className="field"><span>Website</span><input className="input" type="url" placeholder="https://" value={website} onChange={(e) => setWebsite(e.target.value)} /><span className="hint">Used for research later.</span></label>
        <label className="field"><span>Stage</span>
          <select className="input" value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
            {(Object.keys(STAGE_LABELS) as Stage[]).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
          </select>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={create.isPending}>{create.isPending && <span className="spin" />}Create client</button>
        </div>
      </form>
    </Modal>
  );
}

export function ClientDetail() {
  const { id = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const { me, can } = useAuth();
  const tabs = [
    { key: 'overview', label: 'Overview', show: true },
    { key: 'contacts', label: 'Contacts', show: can('crm.view') },
    { key: 'activity', label: 'Activity', show: can('crm.view') },
    { key: 'tasks', label: 'Tasks', show: can('crm.view') },
    { key: 'opportunities', label: 'Opportunities', show: can('crm.view') },
    { key: 'research', label: 'Research', show: can('crm.view') },
    { key: 'projects', label: 'Projects', show: can('delivery.view') },
    { key: 'kits', label: 'UI kits', show: !!me?.is_staff && can('delivery.view') },
    { key: 'access', label: 'Access', show: true },
  ].filter((t) => t.show);
  const tab = tabs.some((t) => t.key === search.get('tab')) ? search.get('tab')! : 'overview';
  const go = (t: string) => setSearch(t === 'overview' ? {} : { tab: t });
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  const { confirm, dialog } = useConfirm();
  const [domain, setDomain] = useState('');
  const [inviting, setInviting] = useState(false);
  useLiveTopics('workspace', `client:${id}`);
  const { data: client } = useQuery({ queryKey: ['client', id], queryFn: () => get<Client>(`/api/clients/${id}`) });
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: () => get<UserRow[]>('/api/users'), enabled: can('users.invite_client') });
  useEffect(() => { if (client) setCrumbs([{ label: 'Clients', to: '/clients' }, { label: client.name }]); }, [client, setCrumbs]);
  const refresh = (c: Client) => { qc.setQueryData(['client', id], c); qc.invalidateQueries({ queryKey: ['clients'] }); };

  const update = useMutation({ mutationFn: (body: Partial<Client>) => patch<Client>(`/api/clients/${id}`, body), onSuccess: refresh, onError: (e: Error) => toast(e.message, true) });
  const addDomain = useMutation({
    mutationFn: () => post<Client>(`/api/clients/${id}/domains`, { domain }),
    onSuccess: (c) => { refresh(c); setDomain(''); toast('Domain added'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const removeDomain = async (d: string) => {
    if (!(await confirm(`Remove ${d}?`, { body: `People at @${d} will be signed out straight away and won't be able to sign in.`, ok: 'Remove', danger: true }))) return;
    try { refresh(await del<Client>(`/api/clients/${id}/domains/${encodeURIComponent(d)}`)); toast('Domain removed'); } catch (e) { toast((e as Error).message, true); }
  };

  if (!client) return <main className="page"><span className="spin muted" /></main>;
  const people = users.filter((u) => u.client?.id === client.id);
  return (
    <Page title={client.name} eyebrow={STAGE_LABELS[client.stage]}
          actions={client.website && <a className="btn" href={client.website} target="_blank" rel="noopener noreferrer"><Icon name="globe" />Website</a>}>
      <nav className="tabs">
        {tabs.map((t) => <a key={t.key} href={`?tab=${t.key}`} className={tab === t.key ? 'active' : ''} onClick={(e) => { e.preventDefault(); go(t.key); }}>{t.label}</a>)}
      </nav>
      {tab === 'projects' && <ClientProjects client={client} />}
      {tab === 'contacts' && <ContactsTab client={client} />}
      {tab === 'activity' && <ActivityTab client={client} />}
      {tab === 'tasks' && <TasksTab client={client} />}
      {tab === 'opportunities' && <OpportunitiesTab client={client} />}
      {tab === 'research' && <ResearchTab client={client} />}
      {tab === 'kits' && <KitsTab client={client} />}
      {tab === 'overview' && <>
      <div className="grid-3">
        <section className="card pad stack">
          <h2>Details</h2>
          <label className="field"><span>Stage</span>
            <select className="input" value={client.stage} disabled={!can('clients.edit')} onChange={(e) => update.mutate({ stage: e.target.value as Stage })}>
              {(Object.keys(STAGE_LABELS) as Stage[]).map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </label>
          <label className="field"><span>Website</span>
            <input className="input" type="url" defaultValue={client.website ?? ''} disabled={!can('clients.edit')} onBlur={(e) => e.target.value !== (client.website ?? '') && update.mutate({ website: e.target.value || null })} />
          </label>
        </section>
      {can('crm.view') && <NextUp client={client} go={go} />}
      {can('crm.view') && <KeyFacts client={client} go={go} />}
      {can('crm.view') && <CrmGlance client={client} go={go} />}
      </div>
      </>}
      {tab === 'access' && <>
      <div className="grid-2">
        <section className="card pad stack">
          <div><h2>Allowed sign-in domains</h2><p className="muted small" style={{ margin: '4px 0 0' }}>{client.name}'s people can only be invited and sign in with these email domains.</p></div>
          <div className="row wrap" style={{ gap: 6 }}>
            {client.domains.map((d) => (
              <span key={d} className="badge" style={{ height: 28, paddingRight: 4 }}><span className="mono">@{d}</span>
                {can('clients.edit') && <button className="btn ghost sm" style={{ height: 22, padding: '0 4px' }} onClick={() => removeDomain(d)} aria-label={`Remove ${d}`}><Icon name="x" /></button>}
              </span>
            ))}
            {!client.domains.length && <span className="muted small">None yet: add one before inviting their people.</span>}
          </div>
          {can('clients.edit') && (
            <form className="row" onSubmit={(e) => { e.preventDefault(); addDomain.mutate(); }}>
              <input className="input mono" placeholder="e.g. msd.com" value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="Domain to allow" />
              <button className="btn" disabled={!domain.trim() || addDomain.isPending}>Add</button>
            </form>
          )}
        </section>
      </div>
      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h2>People with access</h2>{can('users.invite_client') && <button className="btn sm" onClick={() => setInviting(true)} disabled={!client.domains.length} title={client.domains.length ? '' : 'Add an allowed domain first'}><Icon name="plus" />Invite</button>}</div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Last sign-in</th></tr></thead>
            <tbody>{people.map((u) => <tr key={u.id}><td><b>{u.name}</b></td><td className="muted">{u.email}</td><td>{u.role.replace('client_', '')}</td><td className="muted">{ago(u.last_login_at)}</td></tr>)}</tbody>
          </table>
          {!people.length && <div className="empty">Nobody from {client.name} has access yet.</div>}
        </div>
      </section>
      </>}
      {tab === 'access' && can('data.export') && <div style={{ marginTop: 16, maxWidth: 640 }}>
        <ExportsCard clientId={client.id} title="Export everything" blurb={`All Blue Market holds about ${client.name}: CRM, projects, tickets and comments, files, attachments and the activity log.`} /></div>}
      {inviting && <InviteDialog clientId={client.id} clientName={client.name} onClose={() => setInviting(false)} />}
      {dialog}
    </Page>
  );
}
